<h2 class="sr-only">Ergebnisse prüfen</h2>

<div class="flex min-h-0 flex-1 flex-col lg:flex-row">
  <!-- What to go through -->
  <aside class="shrink-0 overflow-y-auto border-b border-border p-2 lg:w-44 lg:border-b-0 lg:border-r">
    <app-filter-rail label="Was prüfen" [groups]="railGroups()" [active]="railActive()" (picked)="pick($event)" />
  </aside>

  <div class="min-h-0 min-w-0 flex-1">
    @if (!loaded()) {
      <div class="grid h-full place-items-center"><app-state-panel state="loading" title="Ergebnisse werden geladen" /></div>
    } @else if (error() && !bundle()) {
      <div class="grid h-full place-items-center overflow-auto">
        <app-state-panel state="error" title="Das Ergebnis konnte nicht geladen werden" [description]="error()">
          <button hlmBtn type="button" variant="outline" (click)="retry()">Erneut laden</button>
          @if (item()) { <button hlmBtn type="button" variant="ghost" (click)="skip()">Nächste Analyse</button> }
        </app-state-panel>
      </div>
    } @else if (ended()) {
      <div class="flex h-full items-center justify-center overflow-auto p-6">
        <div class="flex w-full max-w-xl flex-col gap-5">
          <div>
            <h3 class="text-xl font-bold">{{ list().length ? 'Alles durchgesehen' : 'Hier ist gerade nichts zu tun' }}</h3>
            <p class="mt-1 text-muted-foreground">{{ endText() }}</p>
          </div>
          @if (tally().length) {
            <div class="grid auto-cols-[minmax(0,1fr)] grid-flow-col border-y border-border">
              @for (row of tally(); track row.label; let first = $first) {
                <div class="flex flex-col gap-0.5 py-3 pr-4" [class]="first ? '' : 'border-l border-border pl-4'">
                  <span class="ks-eyebrow">{{ row.label }}</span>
                  <span class="font-mono text-xl font-bold">{{ row.count | number: '1.0-0' : 'de-DE' }}</span>
                </div>
              }
            </div>
          }
          <div class="flex flex-wrap gap-2">
            @for (action of endActions(); track action.label; let first = $first) {
              <button hlmBtn type="button" [variant]="first ? 'default' : 'outline'" (click)="action.run()">{{ action.label }}</button>
            }
          </div>
        </div>
      </div>
    } @else {
      @if (item(); as current) {
      @if (bundle(); as data) {
        <app-analysis-viewer
          class="block h-full"
          [surface]="true"
          [surfaceObjects]="view() === 'detection'"
          [analysis]="data.analysis"
          [filesOverview]="data.files"
          [externalPolygons]="data.polygons"
          [enablePolygonEditing]="canEdit()"
          [showPolygons]="true"
          [canEditMarker]="false"
          (polygonsSynced)="onSynced()"
          (deviationPreferenceChanged)="onSynced()"
        >
          <div surfaceOverlay class="contents">
            @if (view() === 'planning' && planning(); as shown) {
              <app-analysis-viewer class="absolute inset-0 z-[5] block [&>div]:!rounded-none [&>div]:!bg-foreground" [initialView]="planningView" [analysis]="data.analysis" [planningId]="shown.id" [filesOverview]="data.files" [visibleViews]="['2d', '3d']" />
            }
          </div>

          <div railHead>
            <div class="-mx-4 -mt-4 mb-3 h-0.5 bg-secondary" role="progressbar" [attr.aria-valuenow]="pos() + 1" [attr.aria-valuemax]="list().length" aria-label="Fortschritt in dieser Liste">
              <div class="h-full bg-primary transition-all" [style.width.%]="progressPercent()"></div>
            </div>
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <h3 class="truncate text-base font-bold" [title]="title()">{{ title() }}</h3>
                <p class="truncate text-xs text-muted-foreground">{{ place() }}</p>
              </div>
              <span class="-mr-1.5 flex shrink-0 items-center text-xs text-muted-foreground">
                <button hlmBtn type="button" variant="ghost" size="icon" class="!size-6 rounded-md" aria-label="Vorherige Analyse" title="←" [disabled]="!pos()" (click)="previous()"><ng-icon name="lucideChevronLeft" class="size-3.5" /></button>
                <span class="font-mono" [hlmTooltipTrigger]="tallyText()">{{ position() }}</span>
                <button hlmBtn type="button" variant="ghost" size="icon" class="!size-6 rounded-md" aria-label="Nächste Analyse" title="→" [disabled]="saving()" (click)="next()"><ng-icon name="lucideChevronRight" class="size-3.5" /></button>
              </span>
            </div>
            <div class="mt-2 flex flex-wrap items-center gap-1.5">
              <app-stage-chip [stage]="current.stage" />
              @for (mark of marks(); track mark) { <span class="inline-flex h-6 items-center rounded-md border border-border px-1.5 font-mono text-2xs text-muted-foreground">{{ mark }}</span> }
            </div>
            @if (hint()) { <p class="mt-1.5 text-xs text-muted-foreground">{{ hint() }}</p> }
            <div class="relative -mx-4 mt-1 px-4">
              <app-tabs label="Ansicht" [items]="viewTabs()" [value]="view()" (valueChange)="view.set($any($event))" />
              <kbd class="ks-kbd absolute right-4 top-3" [hlmTooltipTrigger]="'Zwischen Erkennung und Planung wechseln'">P</kbd>
            </div>
          </div>

          <section railBody class="px-1.5 py-1.5" [class.hidden]="view() !== 'planning'" aria-label="Planung">
            <div class="flex h-9 items-center justify-between gap-2 px-2.5">
              <span class="text-xs text-muted-foreground">Stand</span>
              <span class="inline-flex h-5 items-center rounded-md px-1.5 font-mono text-2xs" [class]="outcomes[current.planning.outcome].classes">{{ outcomes[current.planning.outcome].label }}</span>
            </div>
            @if (planningMessage(); as message) { <p class="px-2.5 pb-2 text-xs text-muted-foreground">{{ message }}</p> }
            @for (row of planningRows(); track row.key) {
              <div class="flex h-9 items-center justify-between gap-3 px-2.5 text-dense">
                <span class="text-muted-foreground">{{ row.key }}</span><span>{{ row.value }}</span>
              </div>
            }
            @if (current.replanned) { <p class="px-2.5 py-2 text-xs text-muted-foreground">Eine weitere Korrektur ist freiwillig. Erst wenn Sie etwas ändern, wird die Analyse wieder zu Zu planen.</p> }
          </section>

          <div railFoot class="flex shrink-0 flex-col gap-1.5 border-t border-border p-3">
            @if (error(); as message) { <p class="px-1 pb-1 text-xs text-danger" role="alert">{{ message }}</p> }
            @if (dirtyText()) { <p class="px-1 pb-1 text-xs text-accent-foreground" role="status">{{ dirtyText() }}</p> }
            @if (excluding()) {
              <p class="px-1 pb-0.5 text-xs text-muted-foreground">Warum zurückstellen?</p>
              @for (reason of excludeReasons; track reason; let index = $index) {
                <button hlmBtn type="button" variant="ghost" size="sm" class="w-full justify-between px-2 text-left text-dense" (click)="exclude(reason)"><span class="truncate">{{ reason }}</span><kbd class="ks-kbd">{{ index + 1 }}</kbd></button>
              }
              <button hlmBtn type="button" variant="ghost" size="sm" class="w-full justify-between px-2 text-dense" (click)="excluding.set(false)">Abbrechen<kbd class="ks-kbd">Esc</kbd></button>
            } @else if (dirty()) {
              <button hlmBtn type="button" size="sm" class="w-full justify-between px-2" [disabled]="saving()" (click)="confirm()">{{ saving() ? 'Wird gespeichert…' : 'Korrektur übernehmen' }}<kbd class="ks-kbd border-white/40 text-white">F</kbd></button>
              <button hlmBtn type="button" variant="ghost" size="sm" class="w-full justify-between px-2 text-dense" [disabled]="saving()" (click)="discard()">Änderungen verwerfen<kbd class="ks-kbd">Esc</kbd></button>
            } @else if (canUpdate()) {
              @if (inReview() && current.stage !== 'flagged') {
                <button hlmBtn type="button" size="sm" class="w-full justify-between px-2" [disabled]="saving() || !current.canApprove" (click)="confirm()">Stimmt<kbd class="ks-kbd border-white/40 text-white">F</kbd></button>
              }
              @if (current.stage === 'flagged') {
                <button hlmBtn type="button" variant="outline" size="sm" class="w-full justify-between px-2" [disabled]="saving() || !current.canApprove" (click)="confirm()">Stimmt doch<kbd class="ks-kbd">F</kbd></button>
              }
              @if (current.stage === 'replan' && store.canStart()) {
                <button hlmBtn type="button" size="sm" class="w-full justify-between px-2" [disabled]="store.running()" (click)="replan()">Neu planen<kbd class="ks-kbd border-white/40 text-white">R</kbd></button>
              }
              @if (isCheck()) {
                <button hlmBtn type="button" size="sm" class="w-full justify-between px-2" [disabled]="saving()" (click)="confirm()">Passt<kbd class="ks-kbd border-white/40 text-white">F</kbd></button>
              }
              <div class="grid grid-cols-2 gap-1">
                @if (inReview() && current.stage !== 'flagged') {
                  <button hlmBtn type="button" variant="outline" size="sm" class="justify-between px-2 text-dense" [disabled]="saving()" [hlmTooltipTrigger]="'Stimmt nicht, später korrigieren'" (click)="later()">Stimmt nicht<kbd class="ks-kbd">M</kbd></button>
                }
                <button hlmBtn type="button" variant="outline" size="sm" class="justify-between px-2 text-dense" [class.col-span-2]="!inReview() || current.stage === 'flagged'" [disabled]="saving()" [hlmTooltipTrigger]="current.stage === 'review' ? 'Ohne Entscheidung zur nächsten Analyse' : ''" [hlmTooltipDisabled]="current.stage !== 'review'" (click)="next()">Weiter<kbd class="ks-kbd">→</kbd></button>
              </div>
              @if (current.review && current.review.decision !== 'seen') {
                <button hlmBtn type="button" variant="ghost" size="sm" class="h-8 w-full justify-between px-2 text-xs text-muted-foreground" [disabled]="saving()" (click)="undo()">Entscheidung zurücknehmen<kbd class="ks-kbd">U</kbd></button>
              } @else if (current.stage !== 'excluded' && current.stage !== 'running') {
                <button hlmBtn type="button" variant="ghost" size="sm" class="h-8 w-full justify-between px-2 text-xs text-muted-foreground" [disabled]="saving()" (click)="excluding.set(true)">Zurückstellen<kbd class="ks-kbd">Z</kbd></button>
              }
            } @else {
              <button hlmBtn type="button" variant="outline" size="sm" class="w-full justify-between px-2 text-dense" (click)="next()">Weiter<kbd class="ks-kbd">→</kbd></button>
            }
          </div>
        </app-analysis-viewer>
      } @else {
        <div class="grid h-full place-items-center"><app-state-panel state="loading" title="Ergebnis wird geladen" /></div>
      }
      }
    }
  </div>
</div>
